import * as React from 'react';
import styled from 'styled-components';

import { Option } from '@webapp/types/common/option';
import { SocialNetwork } from '@common/types/data/social-network';

import { TracLinkPlatform } from '@services/tracpage/types/trac-link-platform';

import { GenericDropdownBuilder } from '@webapp/controllers/form/dropdown';

import DecorationDropdown from '../decoration-dropdown';
import MenuDropdown from '@webapp/controllers/form/menu-dropdown';

import InputField from '@webapp/components/form/input';

// Icons
import NoneIcon from '@components/basics/icons/social/none';
import SnapIcon from '@components/basics/icons/social/snap';
import TikTokIcon from '@components/basics/icons/social/tiktok';
import FacebookIcon from '@components/basics/icons/social/facebook';
import InstagramIcon from '@components/basics/icons/social/instagram';
import TwitterIcon from '@components/basics/icons/social/twitter';

import { checkStartWith, getPlatformDomain } from '../streaming-platform-input';

// TODO: DEVTEAM-845 - Review the new 'aa' so it always shows first but doesnt save as a new one.
export declare type Platforms =
  | SocialNetwork['network']
  | TracLinkPlatform
  | '';
interface SocialNetworkInputCtrlProps {
  className?: string;
  value?: string;
  placeholder?: string;
  error?: string | null;
  network?: Platforms;
  includeStreaming?: boolean;
  hideMenu?: boolean;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange: (value: string, error?: string | null) => void;
  onNetworkChange: (value: Platforms) => void;
  isGrey?: boolean;
}

const SOCIAL_NETWORKS: Option<Platforms>[] = [
  // TODO: DEVTEAM-845 - 'aa' should not be on the dropdown list to be selectable
  {
    value: '',
    // label: <Translate id="ARTIST_PROFILE.SOCIAL.PLACEHOLDER" />,
    label: 'Add new social profile',
    decoration: <NoneIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('custom', value),
  },
  {
    value: 'facebook',
    label: 'Facebook',
    decoration: <FacebookIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('facebook', value),
  },
  {
    value: 'twitter',
    label: 'Twitter',
    decoration: <TwitterIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('twitter', value),
  },
  {
    value: 'instagram',
    label: 'Instagram',
    decoration: <InstagramIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('instagram', value),
  },
  {
    value: 'snapchat',
    label: 'Snapchat',
    decoration: <SnapIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('snapchat', value),
  },
  {
    value: 'tiktok',
    label: 'TikTok',
    decoration: <TikTokIcon className="social-btn" />,
    validate: (value: string) => checkStartWith('tiktok', value),
  },
];

const validateInputField = (
  platform: Option<Platforms>,
  value: string
): string | null => {
  if (!checkStartWith(platform.value, value)) {
    return getPlatformDomain(platform.value);
  }
  return null;
};

const DropdownCtrl = GenericDropdownBuilder<Platforms>();

export const SocialNetworkInputCtrl: React.FC<SocialNetworkInputCtrlProps> = (
  props
) => {
  const [isOpen, setOpen] = React.useState(false);
  const [menuOpened, setMenuOpened] = React.useState(false);

  const platformList = SOCIAL_NETWORKS;

  const currentOption = platformList.find(
    (s) => s.value === props.network
  ) as Option<Platforms>;

  const menuOptions: Option[] = [
    {
      label: 'ARTIST_PROFILE.SETTINGS.SOCIAL.REMOVE',
      value: 'remove',
      withTranslation: true,
      isRedText: true,
    },
  ];

  const leftDecoration = (
    <DecorationDropdown listOpen={isOpen}>
      {currentOption?.decoration}
    </DecorationDropdown>
  );

  return (
    <Wrapper>
      <DropdownCtrl
        isGrey={props.isGrey ?? false}
        className={props.className}
        title=""
        value={props.network}
        onChange={(value) => props.onNetworkChange(value)}
        options={platformList}
        onOpen={setOpen}
        header={
          <InputField
            isGrey={props.isGrey ?? false}
            placeholder={props.placeholder}
            name="social-network"
            label={currentOption?.label}
            error={props.error}
            autoComplete="off"
            value={props.value}
            leftDecoration={leftDecoration}
            onChange={(value) =>
              props.onChange(value, validateInputField(currentOption, value))
            }
          />
        }
      />
      {!props.hideMenu && (
        <MenuButton
          className={menuOpened ? 'open' : ''}
          onClick={() => setMenuOpened(true)}
        >
          <MenuDropdown
            isSmall
            hasDottedIcon
            menuContent={menuOptions}
            onSelect={props.onClose}
            onClose={() => setMenuOpened(false)}
          />
        </MenuButton>
      )}
    </Wrapper>
  );
};

export default styled(SocialNetworkInputCtrl)`
  .decoration-left {
    width: 20px;
    height: 20px;

    @media ${({ theme }) => theme.device.tablet} {
      width: 28px;
      height: 28px;
    }

    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 20px;
      height: 20px;
      background-color: ${({ theme }) => theme.colors.headerBorder};
      border-radius: 4px;
    }

    @media ${({ theme }) => theme.device.tablet} {
      &:before {
        width: 28px;
        height: 28px;
      }
    }
  }

  .social-btn,
  .platform-btn {
    cursor: pointer;
    width: 10px;
    height: 10px;

    @media ${({ theme }) => theme.device.tablet} {
      width: 14px;
      height: 14px;
    }
  }
`;
const MenuButton = styled.button`
  background-color: ${({ theme }) => theme.colors.secondary};
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translate(0, -50%);
  z-index: 3;
  transition: background-color
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    right: 18px;
  }

  &.open {
    z-index: 4;
  }

  &:hover {
    background-color: ${({ theme }) => theme.colors.secondaryDark};
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Wrapper = styled.div`
  position: relative;
`;
